import { useState, useEffect, useMemo } from 'react';
import type { ObjktListing } from '@/lib/types/objkt';

interface SweepModeToastProps {
  isVisible: boolean;
  itemCount: number;
  totalPriceXtz: string;
  /** Listed items on the current page, sorted by price ascending */
  pageListings: ObjktListing[];
  /** Which listing IDs are currently in the cart */
  cartListingIds: Set<string>;
  onAddToCart: (listing: ObjktListing) => void;
  onRemoveFromCart: (listingId: string) => void;
  onOpenCart: () => void;
}

export function SweepModeToast({
  isVisible,
  itemCount,
  totalPriceXtz,
  pageListings,
  cartListingIds,
  onAddToCart,
  onRemoveFromCart,
  onOpenCart,
}: SweepModeToastProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      requestAnimationFrame(() => setIsAnimating(true));
    } else {
      setIsAnimating(false);
    }
  }, [isVisible]);

  // Sort listings by price and calculate which are in cart
  const sortedListings = useMemo(() => {
    return [...pageListings].sort(
      (a, b) => Number(BigInt(a.priceMutez) - BigInt(b.priceMutez))
    );
  }, [pageListings]);

  // Count how many of the sorted listings are in the cart (from the start)
  const sweepCount = useMemo(() => {
    let count = 0;
    for (const listing of sortedListings) {
      if (cartListingIds.has(listing.id)) {
        count++;
      } else {
        break; // Stop at first non-cart item
      }
    }
    return count;
  }, [sortedListings, cartListingIds]);

  // Calculate total price for slider preview
  const calculateSweepPrice = (count: number): string => {
    let total = BigInt(0);
    for (let i = 0; i < count && i < sortedListings.length; i++) {
      total += BigInt(sortedListings[i].priceMutez);
    }
    const xtz = Number(total) / 1_000_000;
    return xtz.toFixed(2);
  };

  const [sliderValue, setSliderValue] = useState(sweepCount);
  const [previewPrice, setPreviewPrice] = useState(calculateSweepPrice(sweepCount));

  // Sync slider with actual cart state
  useEffect(() => {
    setSliderValue(sweepCount);
    setPreviewPrice(calculateSweepPrice(sweepCount));
  }, [sweepCount, sortedListings]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    setSliderValue(newValue);
    setPreviewPrice(calculateSweepPrice(newValue));
  };

  const handleSliderCommit = () => {
    const targetCount = sliderValue;

    // Add items from start until we reach targetCount
    for (let i = 0; i < targetCount && i < sortedListings.length; i++) {
      const listing = sortedListings[i];
      if (!cartListingIds.has(listing.id)) {
        onAddToCart(listing);
      }
    }

    // Remove items after targetCount
    for (let i = targetCount; i < sortedListings.length; i++) {
      const listing = sortedListings[i];
      if (cartListingIds.has(listing.id)) {
        onRemoveFromCart(listing.id);
      }
    }
  };

  if (!isVisible && !isAnimating) return null;

  const maxSweep = sortedListings.length;

  return (
    <div
      className={`
        fixed bottom-0 left-0 right-0 z-50
        transform transition-transform duration-300 ease-out
        ${isAnimating && isVisible ? 'translate-y-0' : 'translate-y-full'}
      `}
    >
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left side: Item count */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div>
                <span className="font-semibold text-lg">{itemCount}</span>
                <span className="text-white/80 ml-1 text-sm">
                  {itemCount === 1 ? 'item' : 'items'}
                </span>
                <span className="text-white/60 ml-2 text-sm">
                  ({totalPriceXtz} ꜩ)
                </span>
              </div>
            </div>

            {/* Center: Slider for sweep */}
            {maxSweep > 0 && (
              <div className="flex-1 max-w-md flex items-center gap-3">
                <span className="text-sm text-white/80 whitespace-nowrap">Sweep:</span>
                <div className="flex-1 flex items-center gap-2">
                  <input
                    type="range"
                    min={0}
                    max={maxSweep}
                    value={sliderValue}
                    onChange={handleSliderChange}
                    onMouseUp={handleSliderCommit}
                    onTouchEnd={handleSliderCommit}
                    className="w-full h-2 bg-white/30 rounded-lg appearance-none cursor-pointer
                      [&::-webkit-slider-thumb]:appearance-none
                      [&::-webkit-slider-thumb]:w-5
                      [&::-webkit-slider-thumb]:h-5
                      [&::-webkit-slider-thumb]:bg-white
                      [&::-webkit-slider-thumb]:rounded-full
                      [&::-webkit-slider-thumb]:shadow-md
                      [&::-webkit-slider-thumb]:cursor-pointer
                      [&::-moz-range-thumb]:w-5
                      [&::-moz-range-thumb]:h-5
                      [&::-moz-range-thumb]:bg-white
                      [&::-moz-range-thumb]:rounded-full
                      [&::-moz-range-thumb]:border-0
                      [&::-moz-range-thumb]:cursor-pointer"
                  />
                  <span className="text-sm font-medium min-w-[80px] text-right">
                    {sliderValue}/{maxSweep} ({previewPrice} ꜩ)
                  </span>
                </div>
              </div>
            )}

            {/* Right side: Cart button */}
            <button
              onClick={onOpenCart}
              className="flex items-center gap-2 px-4 py-2 bg-white text-purple-600 hover:bg-purple-50 rounded-lg font-medium transition-all active:scale-95 flex-shrink-0"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <span>View Cart</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
